/**
 * Short UI tone for opening the sheet / modal — quiet, synth-like.
 * Uses a singleton AudioContext; fails silently if audio is blocked.
 */
let sharedCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
	if (typeof window === "undefined") return null;
	try {
		if (!sharedCtx || sharedCtx.state === "closed") {
			sharedCtx = new AudioContext();
		}
		return sharedCtx;
	} catch {
		return null;
	}
}

export async function playArchitectOpenChime(): Promise<void> {
	if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		return;
	}
	const ctx = getAudioContext();
	if (!ctx) return;
	try {
		await ctx.resume();
	} catch {
		return;
	}

	const t0 = ctx.currentTime;
	const master = ctx.createGain();
	master.gain.value = 0.055;
	master.connect(ctx.destination);

	// Descending fifth — brief "readout" feel
	const osc = ctx.createOscillator();
	osc.type = "sine";
	osc.frequency.setValueAtTime(990, t0);
	osc.frequency.exponentialRampToValueAtTime(660, t0 + 0.07);

	const band = ctx.createBiquadFilter();
	band.type = "lowpass";
	band.frequency.setValueAtTime(2800, t0);
	band.Q.setValueAtTime(0.7, t0);

	const env = ctx.createGain();
	env.gain.setValueAtTime(0.0008, t0);
	env.gain.exponentialRampToValueAtTime(0.35, t0 + 0.012);
	env.gain.exponentialRampToValueAtTime(0.0008, t0 + 0.11);

	osc.connect(band);
	band.connect(env);
	env.connect(master);

	// High square blink, heavily filtered
	const tick = ctx.createOscillator();
	tick.type = "square";
	tick.frequency.setValueAtTime(2400, t0 + 0.018);
	const bpf = ctx.createBiquadFilter();
	bpf.type = "bandpass";
	bpf.frequency.value = 1800;
	bpf.Q.value = 12;
	const tickEnv = ctx.createGain();
	tickEnv.gain.setValueAtTime(0, t0);
	tickEnv.gain.linearRampToValueAtTime(0.22, t0 + 0.022);
	tickEnv.gain.exponentialRampToValueAtTime(0.0008, t0 + 0.055);

	tick.connect(bpf);
	bpf.connect(tickEnv);
	tickEnv.connect(master);

	osc.start(t0);
	osc.stop(t0 + 0.12);
	tick.start(t0 + 0.015);
	tick.stop(t0 + 0.06);
}
